import React, { useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList } from '../types';
import { adminService } from '../services/admin';

import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminDashboard'>;

export const AdminDashboardScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();

  useEffect(() => {
    // Route guard: verify admin status
    const checkAdminStatus = async () => {
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        await dialog.alert(
          t('common.errors.unauthorized'),
          t('screens.adminDashboard.notAuthorized'),
          'error'
        );
        navigation.goBack();
      }
    };

    checkAdminStatus();
  }, []);

  const navigationCards = [
    {
      icon: '⚙️',
      title: t('screens.adminSettings.title'),
      description: t('screens.adminSettings.dashboardDescription'),
      route: 'AdminSettings' as const,
    },
    {
      icon: '👥',
      title: t('screens.adminDashboard.manageUsers'),
      description: t('screens.adminDashboard.manageUsersDescription'),
      route: 'ManageUsers' as const,
    },
    {
      icon: '🏟️',
      title: t('screens.adminDashboard.manageClubs'),
      description: t('screens.adminDashboard.manageClubsDescription'),
      route: 'ManageClubs' as const,
    },
    {
      icon: '📊',
      title: t('screens.adminDashboard.analytics'),
      description: t('screens.adminDashboard.analyticsDescription'),
      route: 'AdminAnalytics' as const,
    },
    {
      icon: '🗂️',
      title: t('screens.adminListings.title'),
      description: t('screens.adminListings.dashboardDescription'),
      route: 'AdminListings' as const,
    },
    {
      icon: '🛡️',
      title: t('screens.adminModeration.title'),
      description: t('screens.adminModeration.description'),
      route: 'AdminModeration' as const,
    },
    {
      icon: '📋',
      title: t('screens.adminOptionLists.title'),
      description: t('screens.adminDashboard.manageOptionsDescription'),
      route: 'AdminOptionLists' as const,
    },
    {
      icon: '💰',
      title: t('screens.adminPayouts.title'),
      description: t('screens.adminPayouts.dashboardDescription'),
      route: 'AdminPayouts' as const,
    },
    {
      icon: '🔄',
      title: t('screens.adminTransactions.title'),
      description: t('screens.adminTransactions.dashboardDescription'),
      route: 'AdminTransactions' as const,
    },
  ];

  return (
    <View style={styles.wrapper}>
      <View style={[styles.headerContainer, Platform.OS === 'web' && styles.headerContainerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile')}
          >
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('screens.adminDashboard.title')}</Text>
        </View>
      </View>

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.contentContainer}>
          <Text style={styles.subtitle}>
            {t('screens.adminDashboard.subtitle')}
          </Text>

          <View style={styles.cardsGrid}>
            {navigationCards.map((card, index) => (
              <TouchableOpacity
                key={index}
                style={styles.card}
                onPress={() => navigation.navigate(card.route)}
              >
                <Text style={styles.cardIcon}>{card.icon}</Text>
                <Text style={styles.cardTitle}>{card.title}</Text>
                <Text style={styles.cardDescription}>{card.description}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  headerContainer: {
    padding: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerContainerWeb: {
    alignItems: 'center',
  },
  headerContent: {
    width: '100%',
    maxWidth: 1200,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  backText: {
    fontSize: 16,
    color: '#007AFF',
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#333',
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    ...(Platform.OS === 'web' && {
      alignItems: 'center',
    }),
  },
  contentContainer: {
    width: '100%',
    padding: 16,
    ...(Platform.OS === 'web' && {
      maxWidth: 1200,
    }),
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 24,
  },
  cardsGrid: {
    ...(Platform.OS === 'web' && {
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
      gap: 16,
    }),
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    ...(Platform.OS === 'web'
      ? {
          flexBasis: '30%',
          flexGrow: 1,
          maxWidth: 380,
          minWidth: 280,
        }
      : {
          marginBottom: 16,
        }),
  },
  cardIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
    color: '#333',
  },
  cardDescription: {
    fontSize: 14,
    color: '#666',
  },
});
